import {
  type BookkeepingProgressionInfo as BookkeepingProgressionInfoProto,
  BookkeepingStage as BookkeepingStageProto,
  AccessStatus as AccessStatusProto,
  type BookkeepingCycle as BookkeepingCycleProto,
  type WorkItemCompletion as WorkItemCompletionProto,
  type WorkItem as WorkItemProto,
  type PlatformConnection as PlatformConnectionProto,
} from '@taxfyle/api-internal/internal/bookkeeping_pb'
import { Id } from '@/infra/id'
import { match } from '@/utils/pattern-match-util'
import type {
  BookkeepingCycle,
  ProvidedPlatformConnection,
  BookkeepingProgressionInfoProps,
  WorkItem,
  WorkItemCompletion,
} from '@/features/jobs/bookkeeping/BookkeepingProgressionInfo'
import type { BookkeepingStage } from '@/features/jobs/bookkeeping/bookkeeping-stage'
import type { BookkeepingAccessStatus } from '@/features/jobs/bookkeeping/bookkeeping-access-status'
import { safeTimestampToDate } from '@/utils/grpc-util'

export function mapBookkeepingProgressionInfoToProps(
  proto: BookkeepingProgressionInfoProto,
): BookkeepingProgressionInfoProps {
  const platformConnectionProto = proto.getPlatformConnection()
  const platformConnection = platformConnectionProto
    ? mapPlatformConnectionProtoToPlatformConnection(platformConnectionProto)
    : null

  return {
    id: proto.getId(),
    accessStatus: mapAccessStatusProtoToProps(proto.getAccessStatus()),
    stage: mapStageProtoToStage(proto.getStage()),
    platformConnection: platformConnection,
    workItems: proto.getWorkItemsList().map(mapWorkItemsProtoToWorkItem),
    cycles: proto
      .getCyclesList()
      .map(mapBookkeepingCycleProtoToBookkeepingCycle),
  }
}

function mapPlatformConnectionProtoToPlatformConnection(
  proto: PlatformConnectionProto,
): ProvidedPlatformConnection | null {
  const quickBooks = proto.getQuickBooks()
  if (quickBooks) {
    return {
      type: 'QuickBooks',
      quickBooks: {
        connectionId: Id.assert(quickBooks.getConnectionId()),
        realmId: Id.assert(quickBooks.getRealmId()),
        companyName: quickBooks.getCompanyName(),
      },
    }
  }

  const xero = proto.getXero()
  if (xero) {
    return {
      type: 'Xero',
      xero: {
        connectionId: Id.assert(xero.getConnectionId()),
        tenantId: Id.assert(xero.getTenantId()),
        tenantName: xero.getTenantName(),
      },
    }
  }

  const other = proto.getOther()
  if (other) {
    return {
      type: 'Other',
      other: {
        info: other.getInfo(),
      },
    }
  }

  return null
}

function mapStageProtoToStage(proto: BookkeepingStageProto): BookkeepingStage {
  return match(proto, {
    [BookkeepingStageProto.PENDING]: 'Pending',
    [BookkeepingStageProto.COMPLETED]: 'Completed',
    [BookkeepingStageProto.READY_TO_START]: 'ReadyToStart',
    [BookkeepingStageProto.ASSESSING_BOOKS]: 'AssessingBooks',
    [BookkeepingStageProto.CYCLE_IN_PROGRESS]: 'CycleInProgress',
    [BookkeepingStageProto.PROVISIONING_ACCESS]: 'ProvisioningAccess',
    [BookkeepingStageProto.SCOPE_EXTENSION_REQUESTED]:
      'ScopeExtensionRequested',
    [BookkeepingStageProto.BOOKKEEPING_STAGE_UNSPECIFIED]: 'Unspecified',
  })
}

export function mapAccessStatusProtoToProps(
  proto: AccessStatusProto,
): BookkeepingAccessStatus {
  return match(proto, {
    [AccessStatusProto.NOT_REQUESTED]: 'NotRequested',
    [AccessStatusProto.MANAGED_ACCESS_CLIENT_CONNECTION_REQUESTED]:
      'ManagedAccessClientConnectionRequested',
    [AccessStatusProto.MANAGED_ACCESS_CLIENT_CONFIRMATION_REQUESTED]:
      'ManagedAccessClientConfirmationRequested',
    [AccessStatusProto.MANAGED_ACCESS_ADMIN_CONFIRMATION_REQUESTED]:
      'ManagedAccessAdminConfirmationRequested',
    [AccessStatusProto.MANAGED_ACCESS_CONFIRMED]: 'ManagedAccessConfirmed',
    [AccessStatusProto.UNMANAGED_ACCESS_PROVIDER_CONFIRMATION_REQUESTED]:
      'UnmanagedAccessProviderConfirmationRequested',
    [AccessStatusProto.UNMANAGED_ACCESS_CONFIRMED]: 'UnmanagedAccessConfirmed',
    [AccessStatusProto.ACCESS_STATUS_UNSPECIFIED]: 'Unspecified',
  })
}

export function mapBookkeepingCycleProtoToBookkeepingCycle(
  proto: BookkeepingCycleProto,
): BookkeepingCycle {
  const status = proto.getStatus()

  const id = Id.assert(proto.getId())
  const number = proto.getNumber()
  const startedAt = safeTimestampToDate(proto.getStartedAt())

  if (status?.getInProgress()) {
    return {
      id,
      number,
      startedAt,
      status: {
        type: 'InProgress',
      },
    }
  }

  const canceled = status?.getCanceled()
  if (canceled) {
    return {
      id,
      number,
      startedAt,
      status: {
        type: 'Canceled',
        canceledAt: safeTimestampToDate(canceled.getCanceledAt()),
      },
    }
  }

  const completed = status?.getCompleted()
  if (completed) {
    return {
      id,
      number,
      startedAt,
      status: {
        type: 'Completed',
        completedAt: safeTimestampToDate(completed.getCompletedAt()),
        completedById: Id.assert(completed.getCompletedById()),
        items:
          completed
            .getItemsList()
            .map(mapWorkItemsCompletionProtoToWorkItemCompletion) ?? [],
      },
    }
  }

  return {
    id,
    number,
    startedAt,
    status: {
      type: 'Unknown',
    },
  }
}

function mapWorkItemsCompletionProtoToWorkItemCompletion(
  proto: WorkItemCompletionProto,
): WorkItemCompletion {
  return {
    id: Id.assert(proto.getId()),
    completedCount: proto.getCompletedCount(),
    name: proto.getName(),
    description: proto.getDescription(),
  }
}

function mapWorkItemsProtoToWorkItem(proto: WorkItemProto): WorkItem {
  return {
    workItemId: Id.assert(proto.getWorkItemId()),
    inventoryItemId: Id.assert(proto.getInventoryItemId()),
    inventoryItemName: proto.getInventoryItemName(),
    inventoryItemDescription: proto.getInventoryItemDescription(),
    quantity: proto.getQuantity(),
    completedCount: proto.getCompletedCount(),
  }
}
